import React, { useEffect, useMemo, useRef } from 'react';
import { TableView, Pill, Loading } from '@gpstrackit/gtc-react-controls';
import TableRow from '../TableRow';
import { useTableEvents } from '../../hooks/useTableEvents';

const TableEvents = ({ selectedProfileId, selectedEvent, onSelectEvent }) => {
    const tableContainerRef = useRef(null);
    const {
        tableRef,
        riskEvents,
        totalRiskEvents,
        loadingRiskEvents,
        nextTokenRiskEvents,
        pagination,
        handleChangePage,
        noMoreItemsMessage,
        messages,
    } = useTableEvents(selectedProfileId);

    useEffect(() => {
        const handleRowClick = (e) => {
            const row = e.target.closest('.MuiTableRow-root');
            if (!row) return;
            
            const tbody = row.parentElement;
            const rowIndex = Array.from(tbody.children).indexOf(row);
            const clickedEvent = riskEvents[rowIndex];
            if (clickedEvent) onSelectEvent(clickedEvent);
        };

        const tableElement = tableContainerRef.current?.querySelector('.MuiTableBody-root');
        if (tableElement) {
            tableElement.addEventListener('click', handleRowClick);
            return () => tableElement.removeEventListener('click', handleRowClick);
        }
    }, [riskEvents, onSelectEvent]);

    useEffect(() => {
        const tbody = tableContainerRef.current?.querySelector('.MuiTableBody-root');
        if (!tbody || !selectedEvent) return;

        const rows = tbody.querySelectorAll('.MuiTableRow-root');
        rows.forEach((row, index) => {
            if (riskEvents[index]?.id === selectedEvent.id) {
                row.classList.add('selected-row');
            } else {
                row.classList.remove('selected-row');
            }
        });
    }, [selectedEvent, riskEvents]);

    const columns = useMemo(() => [
        { 
            key: 'category',
            title: messages['driverPerformanceConfig_riskEvents_category'],
            show: true,
            export: true,
            render: (row) => (
                <TableRow row={row} selectedEvent={selectedEvent} onSelectEvent={onSelectEvent}>
                    {messages[`driverPerformanceConfig_riskEvents_category_${row?.eventCategory}`] || 'N/A'}
                </TableRow>
            ),
        },
        {
            key: 'eventName',
            title: messages['driverPerformanceConfig_riskEvents_eventName'],
            show: true,
            export: true,
            render: (row) => (
                <TableRow row={row} selectedEvent={selectedEvent} onSelectEvent={onSelectEvent}>
                    {messages[row?.eventName] || 'N/A'}
                </TableRow>
            ),
        },
        {
            key: 'status',
            title: messages['driverPerformanceConfig_riskEvents_status'],
            show: true,
            export: true,
            render: (row) => (
                <TableRow row={row} selectedEvent={selectedEvent} onSelectEvent={onSelectEvent}>
                    <Pill 
                        item={{ 
                            label: row?.status ? messages['driverPerformanceConfig_riskEvents_active'] : messages['driverPerformanceConfig_riskEvents_inactive'], 
                            color: row?.status ? '#66bb6a' : '#bdbdbd' 
                        }} 
                        variant="filled" 
                    />
                </TableRow>
            ),
        }
    ], [selectedEvent, onSelectEvent, messages]);

    useEffect(() => {
        pagination.setColumns(columns);
    }, [columns, pagination]);

    return (
        <section className="risk-events-tab-table" ref={tableContainerRef}>
            {loadingRiskEvents && <Loading overlay show />}
            <TableView
                {...pagination}
                ref={tableRef}
                keyColumn="eventName"
                data={riskEvents}
                total={totalRiskEvents || 0}
                loading={loadingRiskEvents}
                hidePagination={false}
                infiniteScroll
                nextToken={nextTokenRiskEvents}
                noMoreItemsMessage={noMoreItemsMessage}
                onChangePage={handleChangePage}
                disableItemSelection
                hideDragable
            />
        </section>
    );
};

export default React.memo(TableEvents);
